import React from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import { PiGridFourLight, PiTableLight, PiHouseLight, PiUsersLight, PiBriefcaseLight, PiFileLight, PiCalendarLight, PiProjectorScreenChartLight, PiScalesLight, PiChartBarLight, PiChartLineUpLight, PiDatabaseLight, PiGearLight, PiQuestionLight, PiBookOpenTextLight, PiChatTeardropDotsLight, PiBuildingOfficeLight, PiClockLight, PiSparkleLight, PiUserCircleGearLight, PiCheckSquareOffsetLight, PiUsersThreeLight, PiChatsCircleLight, PiListMagnifyingGlassLight, PiUserCircleCheckLight, PiUserSwitchLight, PiTrendUpLight, PiUserListLight, PiCaretRightLight, PiCaretLeftLight, PiFileTextLight, PiCoinsLight, PiLightbulbLight, PiFileMagnifyingGlassLight, PiAddressBookLight, PiDotsThreeVerticalLight, PiDotsThreeVerticalBold, PiPencilSimpleLineLight, PiScrollLight, PiCaretDownLight } from 'react-icons/pi';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import { formatDocumentName } from '../../../utils/functions';
import Badge from './Badge';
import { PiListLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { toggleMobileSidebar } from '../../../store/actions/SidebarActions';

export type Action = {
  label: string;
  onClick: () => void;
  icon?: React.ReactElement;
  variant?: 'primary' | 'neutral' | 'outline' | 'destructive';
  requiresSubscription?: boolean;
  disabled?: boolean;
  /**
   * Hover text. Chiefly the REASON an entry is disabled - a greyed-out row
   * that will not say why is a row that reads as broken.
   */
  title?: string;
  tourId?: string;
};

type HeaderProps = {
  actions?: Action[];
  dropdownActions?: Action[];
  viewMode?: 'grid' | 'table' | 'calendar';
  setViewMode?: (mode: 'grid' | 'table') => void;
  subscriptionActive?: boolean;
  dropdownIcon?: React.ReactElement;
  dropdownLabel?: string;
  teamId?: string;
  renderDropdownAsButtons?: boolean;
  additionalRightElements?: React.ReactNode;
  /**
   * `node` renders in place of the crumb's text - for a crumb that has to
   * animate (a chat title arriving) rather than just read. `name` stays
   * required: it is the accessible name and the hover title.
   */
  breadcrumbs?: Array<{ name: string; href: string; current: boolean; node?: React.ReactNode }>;
  dropdownTourId?: string;
  /**
   * What the back caret does, when plain history is the wrong answer.
   *
   * `history.goBack()` returns to the previous URL, which for a surface that
   * rewrites its own URL (research replaces /research with /research/:id on
   * create) is a step INSIDE the same session rather than out of it. A page
   * that knows what "back" means for it passes it here.
   */
  onBack?: () => void;
};

const Header: React.FC<HeaderProps> = ({ actions = [], dropdownActions = [], viewMode, setViewMode, subscriptionActive = true, dropdownIcon, dropdownLabel, teamId, renderDropdownAsButtons = false, additionalRightElements, breadcrumbs: propBreadcrumbs, dropdownTourId, onBack }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { breadcrumbs: contextBreadcrumbs } = useBreadcrumbs();
  const location = useLocation();
  const history = useHistory();

  // Only show back caret when on a nested page (e.g., /matters/uuid, /files/uuid)
  // Not on root pages like /matters, /files, /dashboard
  const pathSegments = location.pathname.split('/').filter((x) => x);
  const isDeepPage = pathSegments.length > 1;

  const getBreadcrumbName = (segment: string) => {
    const lowerSegment = segment.toLowerCase();
    const nameMap: { [key: string]: string } = {
      'dashboard': t('Dashboard'),
      'files': t('Files'),
      'research': t('Research'),
      'matters': t('Matters'),
      'analytics': t('Analytics'),
      'projects': t('Projects'),
      'marketplace': t('Marketplace'),
      'marketplace-settings': t('Marketplace settings'),
      'messages': t('Messages'),
      'my-clients': t('Clients'),
      'help': t('Help'),
      'user-manual': t('User manual'),
      'feedback': t('Feedback'),
      'support': t('Support'),
      'settings': t('Settings'),
      'account': t('Account'),
      'security': t('Security'),
      'platform-preferences': t('Platform preferences'),
      'ai-studio': t('AI Studio'),
      'organization': t('Organization'),
      'payment-result': t('Payment result'),
      'design-system': t('Design system'),
      'plans': t('Plans'),
      'management': t('Management'),
      'lawyers': t('Lawyers'),
      'finance': t('Finance'),
      'time-tracking': t('Time tracking'),
      'time-keeping': t('Time keeping'),
      'subscriptions': t('Subscriptions'),
      'queries': t('Queries'),
      'admin': t('Admin'),
      'strategy': t('Strategy'),
      'not-found': t('Page not found'),
      '*': t('Page not found'),
    };
    return nameMap[lowerSegment] || segment.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const defaultBreadcrumbs = React.useMemo(() => {
    const pathname = location.pathname;
    const pathnames = pathname.split('/').filter((x) => x);
    return pathnames.map((value, index) => {
      const href = `/${pathnames.slice(0, index + 1).join('/')}`;
      const name = getBreadcrumbName(value);
      return { name, href, current: index === pathnames.length - 1 };
    });
  }, [location.pathname]);

  const resolvedBreadcrumbs = propBreadcrumbs && propBreadcrumbs.length > 0 ? propBreadcrumbs : contextBreadcrumbs.length > 0 ? contextBreadcrumbs : defaultBreadcrumbs;

  /**
   * WHERE THE USER CAME FROM (cal-4).
   *
   * A page reached from a surface that is not its own list - an event opened
   * from the calendar, say - used to build breadcrumbs as if the user had
   * arrived from the list (Tasks › this task), so the calendar was represented
   * nowhere and `history.goBack()` was the only way back. Any caller may now
   * push with `state: { from, fromLabel }`; the origin then leads the
   * breadcrumb trail and the back caret returns to that exact URL, which
   * carries the calendar's own view and date.
   */
  const origin = location.state as { from?: string; fromLabel?: string } | undefined;
  const originFrom = typeof origin?.from === 'string' && origin.from.startsWith('/') ? origin.from : undefined;
  const originLabel = origin?.fromLabel || (originFrom ? getBreadcrumbName(originFrom.split('?')[0].split('/').filter(Boolean)[0] || '') : '');

  const breadcrumbItems = originFrom
    ? [{ name: originLabel, href: originFrom, current: false }, ...resolvedBreadcrumbs.map((c) => ({ ...c }))]
    : resolvedBreadcrumbs;

  // Feature flag badges - show "Beta" next to the root breadcrumb title
  // e.g. "Templates (Beta) > uuid"
  // Cleared for the EU release - no "Early" badges in the header either.
  const betaRoutes: Record<string, string> = {};
  const rootSegment = pathSegments[0]?.toLowerCase() || '';
  const headerBetaLabel = betaRoutes[rootSegment] || null;

  const getIconForBreadcrumb = (name: string) => {
    switch (name.toLowerCase()) {
      case 'dashboard': return <PiHouseLight className="w-4 h-4 me-2" />;
      case 'home': return <PiHouseLight className="w-4 h-4 me-2" />;
      case 'clients': return <PiUserListLight className="w-4 h-4 me-2" />;
      case 'contacts': return <PiAddressBookLight className="w-4 h-4 me-2" />;
      case 'matters': return <PiBriefcaseLight className="w-4 h-4 me-2" />;
      case 'contracts': return <PiScrollLight className="w-4 h-4 me-2" />;
      case 'files': return <PiFileLight className="w-4 h-4 me-2" />;
      case 'tasks': return <PiCheckSquareOffsetLight className="w-4 h-4 me-2" />;
      case 'research': return <PiListMagnifyingGlassLight className="w-4 h-4 me-2" />;
      case 'data sources': return <PiDatabaseLight className="w-4 h-4 me-2" />;
      case 'account': return <PiUserCircleGearLight className="w-4 h-4 me-2" />;
      case 'admin': return <PiUserCircleGearLight className="w-4 h-4 me-2" />;
      case 'people': return <PiUsersLight className="w-4 h-4 me-2" />;
      case 'departments': return <PiBuildingOfficeLight className="w-4 h-4 me-2" />;
      case 'teams': return <PiUsersThreeLight className="w-4 h-4 me-2" />;
      case 'messages': return <PiChatsCircleLight className="w-4 h-4 me-2" />;
      case 'time-keeping': return <PiClockLight className="w-4 h-4 me-2" />;
      case 'projects': return <PiProjectorScreenChartLight className="w-4 h-4 me-2" />;
      case 'marketplace': return <PiScalesLight className="w-4 h-4 me-2" />;
      case 'analytics': return <PiChartBarLight className="w-4 h-4 me-2" />;
      case 'queries': return <PiSparkleLight className="w-4 h-4 me-2" />;
      case 'tables': return <PiTableLight className="w-4 h-4 me-2" />;
      case 'platform-preferences': return <PiGearLight className="w-4 h-4 me-2" />;
      case 'help': return <PiQuestionLight className="w-4 h-4 me-2" />;
      case 'user-manual': return <PiBookOpenTextLight className="w-4 h-4 me-2" />;
      case 'feedback': return <PiChatTeardropDotsLight className="w-4 h-4 me-2" />;
      case 'organization': return <PiBuildingOfficeLight className="w-4 h-4 me-2" />;
      case 'lawyers': return <PiUserCircleCheckLight className="w-4 h-4 me-2" />;
      case 'finder': return <PiUserSwitchLight className="w-4 h-4 me-2" />;
      case 'pipeline': return <PiTrendUpLight className="w-4 h-4 me-2" />;
      case 'templates': return <PiFileTextLight className="w-4 h-4 me-2" />;
      case 'drafting': return <PiPencilSimpleLineLight className="w-4 h-4 me-2" />;
      case 'calendar': return <PiCalendarLight className="w-4 h-4 me-2" />;
      case 'settings': return <PiGearLight className="w-4 h-4 me-2" />;
      case 'finance': return <PiCoinsLight className="w-4 h-4 me-2" />;
      case 'strategy': return <PiLightbulbLight className="w-4 h-4 me-2" />;
      case 'review': return <PiFileMagnifyingGlassLight className="w-4 h-4 me-2" />;
      case 'reviews': return <PiFileMagnifyingGlassLight className="w-4 h-4 me-2" />;
      // Matter Economics pages - icons mirror the sidebar items.
      case 'my time entries': return <PiClockLight className="w-4 h-4 me-2" />;
      case 'firm economics': return <PiChartLineUpLight className="w-4 h-4 me-2" />;
      default: return null;
    }
  };

  const ViewToggle: React.FC = () => {
    if (!viewMode || !setViewMode) return null;
    return (
      <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700">
        <button
          onClick={() => setViewMode('grid')}
          className={clsx(
            'p-1 rounded-s',
            viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
          title={t('Grid view')}
        >
          <PiGridFourLight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={clsx(
            'p-1 rounded-e',
            viewMode === 'table' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
          title={t('Table view')}
        >
          <PiTableLight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 h-12">
      <nav className="flex items-center space-x-2">
        {/* Hamburger — opens the off-canvas sidebar on mobile. Hidden at lg+,
            where the sidebar is always in view. */}
        <button
          type="button"
          onClick={() => dispatch(toggleMobileSidebar())}
          aria-label={t('Open menu')}
          className="lg:hidden -ms-1 me-1 flex items-center justify-center rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
        >
          <PiListLight className="h-5 w-5" />
        </button>
        {(isDeepPage || originFrom || onBack) && (
          <button
            onClick={() => {
              if (onBack) return onBack();
              return originFrom ? history.push(originFrom) : history.goBack();
            }}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title={originFrom ? t('Back to {{page}}', { page: originLabel }) : t('Go back')}
          >
            <PiCaretLeftLight className="w-4 h-4 rtl:-scale-x-100" />
          </button>
        )}
        {breadcrumbItems.map((crumb, index) => {
          // Try to format the crumb name (in case it's a file name)
          const formatted = formatDocumentName(crumb.name);
          const displayName = formatted.extension ? formatted.name : crumb.name;
          const extension = formatted.extension;

          return (
          <React.Fragment key={crumb.href}>
            <>
              {index > 0 && <PiCaretRightLight className='w-4 h-4 text-gray-400' />}
              <NavLink
                to={crumb.href}
                className={clsx(
                  'flex items-center text-sm transition-colors duration-200 truncate',
                  crumb.current
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                )}
                aria-current={crumb.current ? 'page' : undefined}
              >
                {index === 0 && getIconForBreadcrumb(crumb.name)}
                {(crumb as { node?: React.ReactNode }).node ?? t(displayName)}
                {index === 0 && headerBetaLabel && <Badge color="blue" className="ms-1.5 !text-[9px] !px-1 !py-0">{headerBetaLabel}</Badge>}
                {extension && <Badge className="ms-2 text-xs">{extension.toUpperCase()}</Badge>}
              </NavLink>
            </>
          </React.Fragment>
        );
        })}
      </nav>

      {(actions.length > 0 || dropdownActions.length > 0 || viewMode || additionalRightElements) && (
        <div className="flex items-center space-x-2">
          <ViewToggle />
          {additionalRightElements}
          {actions.map((action, index) => (
            <div key={index} {...(action.tourId ? { 'data-tour-id': action.tourId } : {})}>
              <Button
                variant={action.variant || (index === 0 ? 'primary' : 'outline')}
                size="small"
                onClick={action.onClick}
                disabled={(action.requiresSubscription && !subscriptionActive)  ||  action.disabled}
              >
                {action.icon && <span className="me-1">{action.icon}</span>}
                {t(action.label)}
              </Button>
            </div>
          ))}
          {dropdownActions.length > 0 && !renderDropdownAsButtons && (
            <Menu as="div" className="relative inline-block text-start z-50" {...(dropdownTourId ? { 'data-tour-id': dropdownTourId } : {})}>
              <Menu.Button
                as="button"
                type="button"
                title={dropdownLabel ? undefined : t('More')}
                aria-label={dropdownLabel ? undefined : t('More')}
                /*
                  Weighted like every other overflow menu in the app.

                  This was a 16px LIGHT-weight glyph in `text-fg-muted` - three
                  hairline dots, muted grey, on a near-white bar. At that size
                  the Light weight has almost nothing to it, and the control
                  read as disabled or absent; the row menus next to it use a
                  20px mark at `gray-500` and are perfectly legible.

                  So: `text-fg-body`, the same tone as the other controls in
                  this bar, a 20px mark, and a 28px target to put it on the
                  same footing as the buttons beside it.
                */
                className={dropdownLabel
                  /* A labelled "Actions" trigger (feedback round 3, Drafting):
                     a page with many menu entries names the menu instead of
                     hiding it behind three dots. Same height and border as
                     the outline buttons beside it. */
                  ? "inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 text-sm font-medium text-fg-body transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
                  : "flex h-7 w-7 items-center justify-center rounded-md text-fg-body transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"}
              >
                {dropdownLabel ? (
                  <>
                    {t(dropdownLabel)}
                    <PiCaretDownLight className="h-3.5 w-3.5" aria-hidden />
                  </>
                ) : (dropdownIcon || <PiDotsThreeVerticalBold className="h-5 w-5" />)}
              </Menu.Button>
              {/*
                Anchored BELOW the trigger, right edges aligned (R4-02). It was
                portalled and placed by hand from the button's onClick, but
                Headless UI v2 opens on pointer-down - before onClick - so the
                first paint used a stale position and the menu covered its
                own trigger. `anchor` portals it too (never clipped by an
                ancestor overflow-hidden), flips at the viewport edge and
                keeps it in view while scrolling.

                A POPOVER, so it is opaque: `bg-surface` + a hairline ring,
                like every other dropdown in the app.
              */}
              <Menu.Items
                anchor="bottom end"
                transition
                className="z-[9999] [--anchor-gap:6px] [--anchor-padding:8px] w-max min-w-[12rem] max-w-[calc(100vw-16px)] overflow-y-auto origin-top-right rounded-xl bg-surface ring-1 ring-black/[0.05] dark:ring-white/[0.08] shadow-xl focus:outline-none p-1 transition duration-150 ease-pop data-[closed]:opacity-0 data-[closed]:scale-95"
              >
                {dropdownActions.map((action, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        onClick={action.onClick}
                        title={action.title}
                        disabled={(action.requiresSubscription && !subscriptionActive) || action.disabled}
                        className={clsx(
                          'group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-start whitespace-nowrap',
                          action.variant === 'destructive'
                            ? (active ? 'bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400' : 'text-danger-600 dark:text-danger-400')
                            : (active ? 'bg-surface-sunken text-fg-strong' : 'text-fg-body'),
                          (action.requiresSubscription && !subscriptionActive) || action.disabled ? 'opacity-50 cursor-not-allowed' : ''
                        )}
                      >
                        {action.icon && <span>{action.icon}</span>}
                        {t(action.label)}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>
          )}
          {dropdownActions.length > 0 && renderDropdownAsButtons && (
            <>
              {dropdownActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || (index === 0 ? 'primary' : 'destructive')}
                  size="small"
                  onClick={action.onClick}
                  disabled={(action.requiresSubscription && !subscriptionActive) || action.disabled}
                >
                  {action.icon && <span className="me-1">{action.icon}</span>}
                  {t(action.label)}
                </Button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
